import { archiveStudy } from '../../store/actions';

export interface Study {
  study_instance_uid: string;
  patient_id: number;
  doctor_name: string | null;
  modality: string | null;
  study_description: string | null;
  study_creation_date: string | null;
  is_archived: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  is_deleted: boolean;
  patient_id_1: number;
  patient_patient_name: string;
  patient_email: string;
  patient_phone_number: string;
  patient_birth_date: string;
  patient_sex: string;
  patient_weight: number;
  patient_height: number;
  patient_country: string;
  patient_city: string;
  patient_patient_middle_name: string | null;
  patient_patient_last_name: string | null;
  patient_addressline1: string;
  patient_addressline2: string;
  patient_zip: string;
  patient_state: string;
  patient_watchlist: boolean;
  patient_policy_number: string;
  patient_medical_plan: string;
  patient_emergency_contact_name: string;
  patient_emergency_contact_phone: string;
  patient_emergency_contact_relationship: string;
  patient_patient_id: number;
  patient_created_at: string;
  patient_updated_at: string | null;
  patient_deleted_at: string | null;
  patient_is_deleted: boolean;
  isChecked?: boolean;
}

export interface StudyState {
  studies: Study[];
  report: any;
  study: any;
  needsReload: boolean;
  studyDeleted: boolean;
  totalCount: number;
  hasMore: boolean;
  error: any | null;
  loading: boolean;
  success: boolean;
  filtered_conditions: string[];
}

interface Report {
  reports: any[];
  error: any;
}

interface Profile {
  user: any;
  currentOrganization: any;
}

interface Login {
  errorMsg: string;
  loading: boolean;
  error: boolean;
  needsVerification: boolean;
  success: boolean;
}

interface Register {
  registrationError: any | null;
  message: any | null;
  loading: boolean;
  user: any | null;
  success: boolean;
  error: boolean;
}

interface Organization {
  organizations: any[];
  activeOrganization: any;
  error: boolean;
  loading: boolean;
  success: boolean;
}

interface SR {
  error: any;
  needsReload: boolean;
}

interface Role {
  organizations: any[];
  activeOrganization: any;
  error: any;
  loading: boolean;
}

interface Patient {
  patients: any[];
  totalCount: number;
  hasMore: boolean;
  currentPage: number;
  itemsPerPage: number;
  error: any | null;
  loading: boolean;
  success: boolean;
  filtered_conditions: any;
}

interface SR_Report {
  SR_Report: any;
}

export interface State {
  Study?: StudyState;
  Report?: Report;
  Profile?: Profile;
  Login?: Login;
  Register?: Register;
  Organization?: Organization;
  SR?: SR;
  Role?: Role;
  Patient?: Patient;
  SR_Report?: SR_Report;
}

export interface ArchiveStudyPayload {
  items: Study[];
  page: number;
  items_per_page: number;
  filter_options: string[];
}

export interface Menstrual {
  id: number;
  DateOfLastMenstrualPeriod: string;
  AgeAtFirstPeriod: string;
  AgeAtMenopause: string;
  MenstrualCycleLength: string;
  MenstrualFlowDuration: string;
  HeavyPeriods: boolean;
  Painful: boolean;
  Irregular: boolean;
  PMS: boolean; // ask taha about this PMS
}

export interface GlobalReportSearchParams {
  study_id: string;
  serie_id: string;
  date: string;
}

export enum DicomUploadStatus {
  Pending = 'PENDING',
  Uploading = 'UPLOADING',
  Successful = 'SUCCESSFUL',
  Failed = 'FAILED',
}

export interface DicomInstance {
  sop_instance_uid?: string;
  image_type?: string[];
  transfer_syntax_uid?: string;
  instance_number?: number;
  sop_class_uid?: string;
  height?: number;
  width?: number;
  pixel_spacing?: [number, number];
  frames?: number;
  instance_description?: string;
  file?: string | File;
  upload_status?: DicomUploadStatus;
  error?: string;
  error_message?: string;
}

export interface DicomSeries {
  series_instance_uid?: string;
  series_number?: number;
  series_modality?: string;
  physician_name?: string;
  series_institution_name?: string;
  frame_numbers?: number;
  instances: DicomInstance[];
  upload_status?: DicomUploadStatus;
}

export interface DicomStudy {
  study_instance_uid?: string;
  accession_number?: string;
  study_date?: string;
  study_time?: string;
  study_id?: string;
  patient_id?: string;
  patient_name?: string;
  series: DicomSeries[];
  upload_status?: DicomUploadStatus;
}

export type DicomDataStructure = DicomStudy[];

export interface StudieInfo {
  study_instance_uid?: string; //
  study_date?: string; //
  study_time?: string;
  study_id?: string;
  patient_id?: string; //
  patient_name?: string; //
  series_instance_uid?: string; //
  series_modality?: string; //
  sop_instance_uid?: string; //
  transfer_syntax_uid?: string; //
  sop_class_uid?: string; //
  pixel_spacing?: [number, number]; //
  frames?: number; //
  study_upload_status: DicomUploadStatus;
  series_upload_status: DicomUploadStatus;
  instance_upload_status: DicomUploadStatus;
}

export interface StudySerieUploadInfo {
  study_instance_uid: string;
  series_instance_uid: string;
  total_files: number;
  total_failed: number;
  total_success: number;
  failed_instances: DicomInstance[];
}
export interface AddStudie {
  file: string | File;
  StudieInfo: StudieInfo;
}

export interface StudyResult {
  sop_instance_uid: string;
  study_instance_uid: string;
  serie_instance_uid: string;
}

export interface ApiResponse<T> {
  message: string;
  status_code: number;
  data: {
    result: T;
  };
}

// Usage example
export type StudyUploadResponse = ApiResponse<StudyResult>;

export interface ApiErrorDetails {
  [key: string]: any;
}

export interface ApiError {
  error: string;
  message: string;
  status_code: number;
  details?: ApiErrorDetails;
}

// Example of how to use the error interface
export interface StudyUploadError extends ApiError {
  details?: {
    original_study_info?: string;
    original_instance_info?: string | Record<string, any>;
    study_info_dict?: Record<string, any>;
    patient_id?: number;
    filename?: string;
    validation_error?: string;
    dicom_error?: string;
    transfer_syntax?: string;
    sop_instance_uid?: string;
  };
}

export function isApiError(response: any): response is ApiError {
  return (
    response &&
    typeof response.error === 'string' &&
    typeof response.message === 'string' &&
    typeof response.status_code === 'number'
  );
}

interface Patient {
  id: number;
  patient_name: string;
  owner_id: number;
  email: string | null;
  phone_number: string | null;
  birth_date: string;
  patient_middle_name: string | null;
  patient_last_name: string;
  address: string | null;
  policy_number: string | null;
  insurance_provider: string | null;
  emergency_details: string | null;
  patient_id: number;
  archived: boolean;
}

interface Serie {
  sop_instance_uid: string;
  serie_instance_uid: string;
  study_instance_uid: string;
  frames: number;
  modality: string;
  pixel_spacing: [string, string];
  transfer_syntax: string;
  sop_class_uid: string;
  ai_sr_sop_instance_uid: string | null;
  preset_srs_sop_instances_uid: string | null;
  prediction_state: string;
}

interface Study_ {
  study_instance_uid: string;
  user_id: number;
  patient_id: number;
  description: string;
  study_date: string;
  created_at: string;
  updated_at: string | null;
  is_archived: boolean;
  archived_at: string | null;
  patient: Patient;
  serie: Serie;
}
interface PaginatedStudies {
  data: Study_[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
  filtered_conditions: string | null;
}

export type ExamsPaginatedResponse = ApiResponse<PaginatedStudies>;

export interface UpdateCommentRequest {
  study_instance_uid: string;
  comment: string;
}

// New interface for updating study reports with findings
export interface UpdateStudyReportRequest {
  study_instance_uid: string;
  study_date?: string | null;
  frame_id?: string;
  report_content?: any;
  comment?: string;
  maternal_weight_kg?: number | null;
  blood_pressure?: string | null;
  mother_heart_rate?: number | null;
  skeletal_abnormalities?: string | null;
  skeletal_abnormalities_note?: string | null;
  conjunctiva_palms_condition?: string | null;
  breast_exam?: string | null;
  breast_exam_note?: string | null;
  edema?: string | null;
  uterine_height_cm?: number | null;
  heart_rate?: number | null;
  fetal_movement_present?: boolean | null;
  speculum_exam?: string | null;
  speculum_exam_note?: string | null;
  cervical_state?: string | null;
  pelvic_assessment?: string | null;
  urine_nitrites?: string | null;
  urine_leucocytes?: string | null;
  urine_albuminuria?: string | null;
  urine_blood?: string | null;
  urine_ph?: string | null;
  rubeola?: string | null;
  toxoplasmosis?: string | null;
  syphilis_tpha_vdrl?: string | null;
  hbs_ag?: string | null;
  hiv_serology?: string | null;
  hemoglobin_g_dl?: number | null;
  hematocrit_pct?: number | null;
  platelets_giga_l?: number | null;
  glycemia_gaj_g_l?: number | null;
  glycemia_hgpo_a1h_g_l?: number | null;
  glycemia_hgpo_a2h_g_l?: number | null;
  rai_if_rh_negative?: string | null;
  rai_if_rh_negative_note?: string | null;
  other_lab_results?: string | null;
  other_lab_results_note?: string | null;
  supp_iron?: boolean | null;
  supp_iron_note?: string | null;
  supp_vitamin_d?: boolean | null;
  supp_vitamin_d_note?: string | null;
  supp_folic_acid?: boolean | null;
  supp_folic_acid_note?: string | null;
  treatment?: string | null;
  treatment_note?: string | null;
  drug_classes?: string | null;
  drug_classes_note?: string | null;
  notes?: string | null;
  
  presentation?: 'cephalic' | 'breech' | 'transverse';
  placenta_distance_to_cervix_class?: 'normal' | 'low-lying' | 'previa';
  gestational_diabetes?: boolean;
  placenta_localization?: 'fundal' | 'anterior' | 'posterior';
  preeclampsia?: boolean;
  
  fetal_heart_rate?: number;
  fetal_sex?: string;
  pre_eclampsia?: string;
  placenta_distance_to_cervix?: number;
  
  bpd_cm?: number;
  hc_cm?: number;
  ac_cm?: number;
  fl_cm?: number;
  
  pred_ga_bpd?: number;
  pred_ga_hc?: number;
  pred_ga_ac?: number;
  pred_ga_fl?: number;
  pred_ga_aua?: number;
  pred_edd?: number;
  pred_efw_g?: number;
  additional_info?: Record<
    string,
    { source?: 'ai' | 'manual' | null } & Record<string, unknown>
  >;
}

export interface Sweep {
  id: string;
  name?: string;
  recording?: boolean;
  timer?: number;
  displayTimer?: number;
  completed?: boolean;
  videoData?: string | null;
  sop_instance_uid?: string | null;
  serie_instance_uid?: string | null;
  study_instance_uid?: string | null;
  prediction_state?: string | null;
  video_url?: string | null;
}
export interface PredictionResults{
  Heart_rate?:string | number | null | undefined;
  fetal_presentation?:string | number | null | undefined;
  Placenta_localization?:string | number | null | undefined;
  preeclampsia?:string | number | null | undefined;
  gestational_diabetes?:string | number | null | undefined;
  gestational_age_days?:string | number | null | undefined;
  fetal_weight?:string | number | null | undefined;
  fetal_sex?:string | number | null | undefined;
  is_twins?:Boolean;
  placenta_distance?:string | number | null | undefined;
}
export enum BlindSweepMode {
  UPLOAD = 'upload',
  RECORD = 'record',
}

export enum UploaderType {
  VIDEO = 'video',
  DICOM = 'dicom',
}

export interface BlindSweep {
  mode?: BlindSweepMode;
  uploader?: UploaderType;
}
